'use client';

import { IconMusicHeart } from '@tabler/icons-react';
import { ChangeEvent, FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Flex } from '@mantine/core';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import Link from 'next/link';
import Image from 'next/image';
import classes from './ToggleRadioButton.module.css';
import { doGetRadioListeners, doListenToRadio, getFullMetdata, getRadioListeners, RadioMetadata } from '@/app/actions';
import { ScrollingTitle } from './ScrollingTitle';
import { UserDoc } from '@/lib/vodTypes';

export const ToggleRadioButton: FC = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [listeners, setListeners] = useState(0);
    const [peakListeners, setPeakListeners] = useState(0);
    const [title, setTitle] = useState<ReactNode>(null);
    const [radioListeners, setRadioListeners] = useState<UserDoc[]>([]);
    const [metadata, setMetadata] = useState<RadioMetadata | null>(null);

    useEffect(() => {
        if (!isPlaying) {
            return;
        }

        const t = setInterval(async () => {
            doListenToRadio();
            const l = await doGetRadioListeners();
            setRadioListeners(l);
        }, 5000);

        // eslint-disable-next-line consistent-return
        return () => {
            clearInterval(t);
        };
    }, [isPlaying]);

    useEffect(() => {
        const listener_images = radioListeners.map(l => (
            <Image
              key={l.userName}
              src={l.profile_image_url}
              alt={l.displayName}
              width={40}
              height={40}
              style={{ borderRadius: '50%', padding: '5px' }}
            />
        ));

        let t = null;
        if (metadata) {
            if (metadata.type === 'vod') {
                const ts = DateTime.fromJSDate(metadata.streamed_at).toFormat('dd.MM.yyyy');
                t = metadata.id ?
                    <Link className={classes.navItem} href={`/vods/${metadata.id}`}><strong>{ ts }</strong>: {metadata.title}</Link> :
                    <span><strong>{ ts }</strong>: {metadata.title}</span>;
            } else if (metadata.type === 'live') {
                t = <span>{ metadata.title }</span>;
            }
        }

        setTitle(<Flex align="center" gap={5}>{t} {listener_images}</Flex>);
    }, [radioListeners, metadata]);

    const updateMetadata = useCallback(async () => {
        const full = await getFullMetdata();
        setMetadata(full);
    }, []);

    useEffect(() => {
        updateMetadata();

        const t = setInterval(() => {
            updateMetadata();
        }, 10000);

        return () => clearInterval(t);
    }, []);

    const updateListeners = useCallback(async () => {
        const stats = await getRadioListeners();
        setListeners(stats.current);
        setPeakListeners(stats.peak);
    }, []);

    useEffect(() => {
        if (!audioRef.current) return;

        audioRef.current.onloadeddata = () => {
            audioRef.current?.play().catch((error) => {
                console.error('Error playing audio:', error);
            });
        };

        audioRef.current.onplay = () => {
            setIsPlaying(true);

            setTimeout(() => {
                updateListeners();
            }, 5000);
        };

        audioRef.current.onended = () => {
            setIsPlaying(false);

            setTimeout(() => {
                updateListeners();
            }, 5000);
        };

        audioRef.current.onerror = () => {
            console.error('Error loading audio stream');
            setIsPlaying(false);

            setTimeout(() => {
                updateListeners();
            }, 5000);
        };
    }, []);

    useEffect(() => {
        updateListeners();

        const updateInterval = setInterval(() => {
            updateListeners();
        }, 30000);

        return () => clearInterval(updateInterval);
    }, [updateListeners]);

    const togglePlayPause = useCallback(() => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.src = '';
            audioRef.current.load();
        } else {
            audioRef.current.src = 'https://vods.narilah.de/radio.mp3';
            audioRef.current.load();
        }

        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    const handleVolumeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (!audioRef.current) return;

        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        audioRef.current.volume = newVolume;

        if (newVolume === 0) {
            setIsPlaying(false);
            audioRef.current.src = '';
            audioRef.current.load();
            setVolume(1);
        }
    }, []);

    return (
        <Flex align="center" justify="center" title={`Nari-Radio: ${listeners} Hörer (Rekord: ${peakListeners})`}>
            <audio ref={audioRef}>
                <track kind="captions" srcLang="en" label="English" />
            </audio>
            <div className={classes.buttonWithVolume}>
                <button type="button" className={classNames(classes.radioButton, { [classes.paused]: !isPlaying })} onClick={togglePlayPause}>
                    <IconMusicHeart style={{ marginRight: '5px' }} />{ listeners > 0 && (`${listeners}`) }
                </button>
                {
                    isPlaying && (
                        <div className={classes.volumeSliderContainer}>
                            <input
                              className={classes.volumeSlider}
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={volume}
                              onChange={handleVolumeChange}
                            />
                        </div>
                    )
                }
                { title && (<ScrollingTitle className={classes.scrollingTitle}><span>{title}</span></ScrollingTitle>) }
            </div>
        </Flex>
    );
};
